import React, { useState, useEffect, useCallback } from "react";
import { useEmployerJobApi } from "../../../_common/hooks/actions/job/appEmployerJobApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import parse from "html-react-parser";
import { EXTERNAL_URL } from "src/_config";

interface ViewProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: string;
  jobseekerId?: number;
  candidateDetails?: any; // New prop for candidate details from screening API
}

const ViewProfileModal: React.FC<ViewProfileModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobseekerId,
  candidateDetails,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("job-details");
  const jobApi = useEmployerJobApi();
  const S3_BASE_URL = "https://drjobscv-replication.s3.us-east-2.amazonaws.com/jobseekers/";

  const formatDateOfBirth = (dateString: string) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date of birth:", error);
      return dateString; // Return original if error
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      console.log("Candidate Details:", candidateDetails);
    }
  }, [isOpen]);
  if (!isOpen) return null;

  return (
    <div className="view-profile-modal-overlay">
      <div className="view-profile-modal">
        {/* Header */}
        <div className="view-profile-header">
          <div className="header-left">
            {/* <button className="back-btn" onClick={onClose}>
              <i className="fa fa-arrow-left"></i>
              {t("jobDetails.viewProfile.goBack")}
            </button> */}
          </div>
          <div className="header-right">
            <button className="close-btn" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="view-profile-content">
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <p>{t("jobDetails.viewProfile.loading")}</p>
            </div>
          ) : (
            <>
              {/* Profile Header Section */}
              <div className="profile-header-section">
                <div className="profile-avatar-centered">
                  <img
                    src={candidateDetails?.avatar || "/images/user-avatar.jpg"}
                    alt={candidateDetails?.name || t("jobDetails.viewProfile.unknownCandidate")}
                  />
                </div>
                <div className="profile-info">
                  <h1 className="profile-name">{candidateDetails?.name}</h1>
                  <p className="profile-title">
                    {candidateDetails?.currentDesignation +
                      (candidateDetails?.currentLocation
                        ? (candidateDetails?.currentDesignation ? " | " : "") + candidateDetails?.currentLocation
                        : "")}
                  </p>

                  <div className="profile-details-tags">
                    {candidateDetails?.totalExperienceYear && (
                      <span className="detail-tag experience-tag">
                        <i className="fa fa-calendar"></i>
                        {candidateDetails.totalExperienceYear} {t("jobDetails.viewProfile.year")}
                      </span>
                    )}
                    {candidateDetails?.gender && candidateDetails.gender !== "-" && (
                      <span className="detail-tag gender-tag">
                        <i className="fa fa-venus"></i>
                        {candidateDetails.gender}
                      </span>
                    )}
                    {candidateDetails?.currentSalary && candidateDetails?.currentSalaryCurrency && (
                      <span className="detail-tag salary-tag">
                        <i className="fa fa-dollar"></i>
                        {candidateDetails.currentSalary}
                        {candidateDetails.currentSalaryCurrency}
                      </span>
                    )}
                  </div>

                  <div className="profile-actions">
                    {candidateDetails?.basicDetails?.resume_file && (
                      <button
                        className="btn-cancel"
                        onClick={() => {
                          if (candidateDetails?.basicDetails?.resume_file) {
                            // The resume_file already contains the full path from the API
                            const resumeUrl = candidateDetails.basicDetails.resume_file.startsWith("http")
                              ? candidateDetails.basicDetails.resume_file
                              : `${candidateDetails.basicDetails.resume_file}`;
                            window.open(resumeUrl, "_blank");
                          }
                        }}
                        disabled={!candidateDetails?.basicDetails?.resume_file}
                      >
                        <i className="fa fa-download"></i>
                        {t("jobDetails.viewProfile.download")}
                      </button>
                    )}
                    {candidateDetails?.basicDetails?.video_file && (
                      <button
                        className="btn-preview"
                        onClick={() => {
                          if (candidateDetails?.basicDetails?.video_file) {
                            // The video_file already contains the full path from the API
                            const videoUrl = candidateDetails.basicDetails.video_file.startsWith("http")
                              ? candidateDetails.basicDetails.video_file
                              : `${candidateDetails.basicDetails.jobseeker_id || jobseekerId || ""}/${
                                  candidateDetails.basicDetails.video_file
                                }`;
                            window.open(videoUrl, "_blank");
                          }
                        }}
                        disabled={!candidateDetails?.basicDetails?.video_file}
                      >
                        <i className="fa fa-video-camera"></i>
                        {t("jobDetails.viewProfile.videoCv")}
                      </button>
                    )}
                    {candidateDetails?.jobseeker_public_profiles &&
                      candidateDetails.jobseeker_public_profiles.length > 0 &&
                      candidateDetails.jobseeker_public_profiles[0]?.public_profile_url && (
                        <button
                          className="btn-preview"
                          onClick={() => {
                            const publicProfileUrl = candidateDetails.jobseeker_public_profiles[0].public_profile_url;
                            const baseUrl = EXTERNAL_URL;
                            const fullUrl = `${baseUrl}profile/${publicProfileUrl}`;
                            window.open(fullUrl, "_blank");
                          }}
                        >
                          <i className="fa fa-user"></i>
                          {t("jobDetails.viewProfile.publicProfile", "Public Profile")}
                        </button>
                      )}
                    {/* Show message when files are not available */}
                    {/* {!candidateDetails?.basicDetails?.resume_file && !candidateDetails?.basicDetails?.video_file && (
                      <p style={{ fontSize: "14px", color: "#666", fontStyle: "italic" }}>
                        {t("jobDetails.viewProfile.filesNotAvailable")}
                      </p>
                    )} */}
                  </div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="profile-sections">
                {/* Skills Section */}
                {candidateDetails?.skills && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.skills")}</h3>
                    <p className="section-content">{candidateDetails?.skills}</p>
                  </div>
                )}

                {/* Profile Section */}
                {candidateDetails?.profileSummary && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.profile")}</h3>
                    <div className="section-content">
                      <p>{candidateDetails?.profileSummary}</p>
                    </div>
                  </div>
                )}

                {/* Work Experience Section */}
                {candidateDetails?.employmentDetails && candidateDetails.employmentDetails.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.workExperience")}</h3>
                    <div className="experience-list two-column-layout">
                      {candidateDetails.employmentDetails.map((emp: any, index: number) => {
                        let duration = "";
                        if (!duration && emp.from_period) {
                          try {
                            // Handle different date formats
                            const getDateValue = (dateObj: any) => {
                              if (!dateObj) return null;

                              // Handle ISO date strings (e.g., "1999-01-01T20:00:00.000000Z")
                              if (typeof dateObj === "string") {
                                return new Date(dateObj);
                              }

                              // Handle MongoDB date format with $numberLong
                              if (dateObj.$date && typeof dateObj.$date === "object" && dateObj.$date.$numberLong) {
                                return new Date(parseInt(dateObj.$date.$numberLong));
                              }
                              // Handle MongoDB date format with direct $date
                              if (dateObj.$date) {
                                return new Date(dateObj.$date);
                              }
                              // Handle regular date objects/timestamps
                              return new Date(dateObj);
                            };

                            const fromDate = getDateValue(emp.from_period);
                            const toDate = emp.currently_working ? null : getDateValue(emp.to_period);

                            const fromDateStr =
                              fromDate && !isNaN(fromDate.getTime())
                                ? fromDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                  })
                                : t("jobDetails.viewProfile.unknown");

                            const toDateStr =
                              toDate && !isNaN(toDate.getTime())
                                ? toDate.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                  })
                                : t("jobDetails.viewProfile.present");

                            duration = `${fromDateStr} - ${toDateStr}`;
                          } catch (error) {
                            console.error("Error parsing date:", error);
                            duration = t("jobDetails.viewProfile.dateNotAvailable");
                          }
                        }

                        return (
                          <div key={index} className="experience-item">
                            <div className="experience-icon">
                              <i className="fa fa-briefcase"></i>
                            </div>
                            <div className="experience-details">
                              <h4 className="experience-company">{emp?.designation}</h4>
                              <p className="experience-position">{emp?.company_name}</p>
                              <p className="experience-duration">{duration}</p>
                              <div className="experience-description">
                                {emp?.job_desc ? (
                                  Array.isArray(emp.job_desc) ? (
                                    <ul>
                                      {emp.job_desc.map((desc: string, idx: number) => (
                                        <li key={idx}>{desc}</li>
                                      ))}
                                    </ul>
                                  ) : typeof emp.job_desc === "string" ? (
                                    parse(emp.job_desc)
                                  ) : null
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Education Section */}
                {candidateDetails?.educationDetails?.filter(
                  (education: any) => education?.institute_name || education?.degree
                )?.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.education")}</h3>
                    <div className="education-list two-column-layout">
                      {candidateDetails.educationDetails.map((education: any, index: number) =>
                        education?.institute_name || education?.degree ? (
                          <div key={index} className="education-item">
                            <div className="education-icon">
                              <i className="fa fa-graduation-cap"></i>
                            </div>
                            <div className="education-details">
                              <h4 className="education-degree">{education?.institute_name}</h4>
                              <p className="education-institution">{education?.degree}</p>
                              <p className="education-duration">
                                {education?.completion_year ? "Completion Year - " + education?.completion_year : ""}
                              </p>
                            </div>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}

                {/* Course Section */}
                {candidateDetails?.courseDetails && candidateDetails.courseDetails.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.courses")}</h3>
                    <div className="education-list two-column-layout">
                      {candidateDetails.courseDetails.map((course: any, index: number) => (
                        <div key={index} className="education-item">
                          <div className="education-icon">
                            <i className="fa fa-book"></i>
                          </div>
                          <div className="education-details">
                            <h4 className="education-degree">{course.course_name}</h4>
                            <p className="education-institution">{course.institute_name}</p>
                            <p className="education-duration">{course.completion_year}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Projects Section */}
                {candidateDetails?.projectDetails && candidateDetails.projectDetails.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.projects")}</h3>
                    <div className="education-list two-column-layout">
                      {candidateDetails.projectDetails.map((project: any, index: number) => (
                        <div key={index} className="education-item">
                          <div className="education-icon">
                            <i className="fa fa-code"></i>
                          </div>
                          <div className="education-details">
                            <h4 className="education-degree">{project?.project_title}</h4>
                            <p className="education-institution">
                              {project?.organization_name}
                              {project?.project_url ? " - " + project?.project_url : ""}
                            </p>
                            <p className="education-duration">{project?.duration}</p>
                            <div className="education-institution">
                              {project?.project_description ? (
                                typeof project.project_description === "string" ? (
                                  parse(project.project_description)
                                ) : Array.isArray(project.project_description) ? (
                                  <ul>
                                    {project.project_description.map((desc: string, idx: number) => (
                                      <li key={idx}>{desc}</li>
                                    ))}
                                  </ul>
                                ) : null
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certificates Section */}
                {candidateDetails?.certificateDetails && candidateDetails.certificateDetails.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.certificates")}</h3>
                    <div className="certificates-list two-column-layout">
                      {candidateDetails.certificateDetails.map((cert: any, index: number) => (
                        <div key={index} className="certificate-item">
                          <div className="certificate-icon">
                            <i className="fa fa-trophy"></i>
                          </div>
                          <div className="certificate-details">
                            <h4 className="certificate-name">{cert?.certificate_name}</h4>
                            <p className="certificate-issuer">
                              {cert?.institute_name} {cert.completion_location ? " - " + cert.completion_location : ""}
                            </p>
                            {cert?.completion_year}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Personal Details Section */}
                <div className="profile-section">
                  <h3 className="section-title">{t("jobDetails.viewProfile.sections.personalDetails")}</h3>
                  <div className="personal-details-grid">
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.currentDesignation")}</h4>
                      <p className="detail-value">{candidateDetails?.currentDesignation || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.currentLocation")}</h4>
                      <p className="detail-value">{candidateDetails?.currentLocation || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.languages")}</h4>
                      <p className="detail-value">{candidateDetails?.languages || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.email")}</h4>
                      <p className="detail-value">{candidateDetails?.basicDetails?.email || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.mobileNumber")}</h4>
                      <p className="detail-value">{candidateDetails?.basicDetails?.mobile_number || "-"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewProfileModal;
